import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Interweave from 'interweave'
import { sortBy, isEmpty } from 'lodash'
import { convertMdToHtml } from '..'
import {
  FINDING_HTML_ID_ATTRIBUTE,
  OTHER_TITLE_ID,
  SEVERITY,
  SEVERITY_DESCRIPTION,
  UPDATE_STATUS,
} from '../../../constants'
import { getSeverityLabelType, getStatusLabelType } from '../utils'
import Section from '../../Section'
import Link from '../../Link'
import Label from '../../Label'
import Alert from '../../Alert'
import Badge from '../../Badge'

const FindingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
`

const FindingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;

  @media screen and (max-width: 1200px) {
    flex-direction: column;
    align-items: baseline;
  }
`

const FindingHeaderDetails = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`

const FindingTitle = styled.h3`
  font-weight: 600;
  font-size: 24px;
  line-height: 30px;

  ${Link}, p {
    display: inline-block;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`

const Findings = ({ report: { reportContent } }) => (
  <Section title="Findings">
    {!isEmpty(reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray) &&
      (reportContent.breakdownAndAutomatedAnalysis.sortBySeverity
        ? sortBy(reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray, v =>
            parseInt(v.severity.value, 10),
          )
        : reportContent.breakdownAndAutomatedAnalysis.vulnerabilitiesArray
      ).map((vulnerability, index) => {
        const findingIdLetters = reportContent.breakdownAndAutomatedAnalysis.vulnIndex || 'QS'
        const findingId = index + 1

        return (
          <FindingContainer key={findingId} id={`${FINDING_HTML_ID_ATTRIBUTE}${findingId}`}>
            <FindingHeader>
              <FindingTitle>
                <Link href={`#${FINDING_HTML_ID_ATTRIBUTE}${findingId}`}>
                  {findingIdLetters}-{findingId}
                </Link>
                &nbsp;&nbsp;
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    Number(vulnerability.title.value) !== OTHER_TITLE_ID
                      ? vulnerability.title.label
                      : vulnerability.otherTitle,
                  )}
                />
              </FindingTitle>
              <FindingHeaderDetails>
                <Label
                  type={getSeverityLabelType(vulnerability.severity.value)}
                  tooltip={{
                    id: `${findingId}-${vulnerability.title.label}`,
                    content: SEVERITY_DESCRIPTION[vulnerability.severity.value],
                  }}
                >
                  {SEVERITY[vulnerability.severity.value].label}
                </Label>
                {vulnerability.status ? (
                  <Badge type={getStatusLabelType(vulnerability.status.value)}>
                    {vulnerability.status.label}
                  </Badge>
                ) : null}
              </FindingHeaderDetails>
            </FindingHeader>
            {vulnerability.updates?.length &&
              vulnerability.updates.map((update, i) =>
                !isEmpty(update.description) ? (
                  <div key={i}>
                    <Alert type={update.type?.value}>
                      <b>{update.type?.value === UPDATE_STATUS[2].value ? 'Alert' : 'Update'}</b>
                      <Interweave content={convertMdToHtml.makeHtml(update.description)} />
                    </Alert>
                  </div>
                ) : null,
              )}
            {!isEmpty(vulnerability.contracts[0]?.name) && (
              <div>
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    '**File(s) affected:** ' +
                      vulnerability.contracts.map(c => (c.name ? ` \`${c.name}\`` : '')),
                  )}
                />
              </div>
            )}
            {!isEmpty(vulnerability.relatedIssues[0]?.swc) && (
              <div>
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    '**Related Issue(s):** ' +
                      vulnerability.relatedIssues.map(i =>
                        i.swc
                          ? ` [SWC-${i.swc}](https://smartcontractsecurity.github.io/SWC-registry/docs/SWC-${i.swc})`
                          : '',
                      ),
                  )}
                />
              </div>
            )}
            {!isEmpty(vulnerability.description) && (
              <div>
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    '**Description:** ' + vulnerability.description,
                  )}
                />
              </div>
            )}
            {!isEmpty(vulnerability.exploitScenario) && (
              <div>
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    '**Exploit Scenario:** ' + vulnerability.exploitScenario,
                  )}
                />
              </div>
            )}
            {!isEmpty(vulnerability.recommendation) && (
              <div>
                <Interweave
                  content={convertMdToHtml.makeHtml(
                    '**Recommendation:** ' + vulnerability.recommendation,
                  )}
                />
              </div>
            )}
          </FindingContainer>
        )
      })}
  </Section>
)

Findings.propTypes = {
  report: PropTypes.object,
}

export default Findings
